import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { BaseService } from './base.service';
import {
  WeekSlots,
  SlotRequestParams,
  DaySlot,
  TimeSlot,
} from '../interfaces/slot.interface';

@Injectable({
  providedIn: 'root',
})
export class SlotService extends BaseService {
  constructor(http: HttpClient) {
    super(http);
  }

  /**
   * Obtenir les créneaux d'un médecin pour une semaine donnée
   */
  getDoctorSlots(params: SlotRequestParams): Observable<WeekSlots> {
    const httpParams = new HttpParams()
      .set('doctorId', params.doctorId.toString())
      .set('startDate', params.startDate)
      .set('endDate', params.endDate)
      .set('includeBooked', params.includeBooked?.toString() || 'true');

    return this.http
      .get<WeekSlots>(`${this.baseUrl}/doctors/${params.doctorId}/slots`, {
        headers: this.getHeaders(),
        params: httpParams,
      })
      .pipe(
        catchError(() => {
          // En cas d'erreur, retourner des données de démonstration
          return of(
            this.generateMockSlots(
              params.doctorId,
              params.startDate,
              params.endDate
            )
          );
        })
      );
  }

  /**
   * Réserver un créneau
   */
  bookSlot(
    doctorId: number,
    slotId: string,
    appointmentData: any
  ): Observable<any> {
    return this.http.post(
      `${this.baseUrl}/doctors/${doctorId}/slots/${slotId}/book`,
      appointmentData,
      {
        headers: this.getHeaders(),
      }
    );
  }

  /**
   * Libérer un créneau
   */
  releaseSlot(doctorId: number, slotId: string): Observable<any> {
    return this.http.delete(
      `${this.baseUrl}/doctors/${doctorId}/slots/${slotId}`,
      {
        headers: this.getHeaders(),
      }
    );
  }

  /**
   * Générer des créneaux de démonstration
   */
  private generateMockSlots(
    doctorId: number,
    startDate: string,
    endDate: string
  ): WeekSlots {
    const days: DaySlot[] = [];
    const start = new Date(startDate);
    const end = new Date(endDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Générer les créneaux pour chaque jour de la semaine
    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      const dayName = this.getDayName(d.getDay());
      const dayNumber = d.getDate();
      const dateStr = d.toISOString().split('T')[0];
      const isToday = d.getTime() === today.getTime();
      const isPast = d < today;

      const slots: TimeSlot[] = this.generateDaySlots(dateStr, isPast);

      days.push({
        date: dateStr,
        dayName,
        dayNumber,
        isToday,
        isPast,
        slots,
      });
    }

    return {
      doctorId,
      weekStart: startDate,
      weekEnd: endDate,
      days,
    };
  }

  /**
   * Générer les créneaux pour une journée
   */
  private generateDaySlots(date: string, isPast: boolean): TimeSlot[] {
    const slots: TimeSlot[] = [];
    const startHour = 9; // 9h
    const endHour = 17; // 17h
    const slotDuration = 30; // 30 minutes

    for (let hour = startHour; hour < endHour; hour++) {
      for (let minute = 0; minute < 60; minute += slotDuration) {
        const startTime = `${hour.toString().padStart(2, '0')}:${minute
          .toString()
          .padStart(2, '0')}`;
        const endMinute = minute + slotDuration;
        const endHourAdjusted = hour + Math.floor(endMinute / 60);
        const endMinuteAdjusted = endMinute % 60;
        const endTime = `${endHourAdjusted
          .toString()
          .padStart(2, '0')}:${endMinuteAdjusted.toString().padStart(2, '0')}`;

        // Générer aléatoirement si le créneau est disponible ou réservé
        const isBooked = isPast ? false : Math.random() > 0.7; // 30% de chance d'être réservé
        const isAvailable = !isPast && !isBooked;

        slots.push({
          id: `${date}-${startTime}`,
          startTime,
          endTime,
          isAvailable,
          isBooked,
          appointmentId: isBooked
            ? `app-${Math.random().toString(36).substr(2, 9)}`
            : undefined,
        });
      }
    }

    return slots;
  }

  /**
   * Obtenir le nom du jour en français
   */
  private getDayName(dayIndex: number): string {
    const days = [
      'Dimanche',
      'Lundi',
      'Mardi',
      'Mercredi',
      'Jeudi',
      'Vendredi',
      'Samedi',
    ];
    return days[dayIndex];
  }

  /**
   * Obtenir le nom court du jour en français
   */
  getShortDayName(dayIndex: number): string {
    const days = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];
    return days[dayIndex];
  }

  /**
   * Formater l'heure pour l'affichage
   */
  formatTime(time: string): string {
    return time;
  }

  /**
   * Vérifier si un créneau est sélectionnable
   */
  isSlotSelectable(slot: TimeSlot): boolean {
    return slot.isAvailable;
  }
}
